import { CONFIG } from "../../config";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";
import {
  ConnectToPosResponse,
  ManualOrderItem,
  PosCredentials,
  PosItem,
  PosOrder,
  PosProviderType,
  PosTable,
} from "../../interfaces/posSimulator.interface";

const BASE = CONFIG.SERVER.BASE_URL + "/pos";

// POST /pos/connect
export async function connectToPos(
  businessId: string,
  posProvider: PosProviderType,
  credentials?: PosCredentials
): Promise<ConnectToPosResponse> {
  const axios = await getTokenizedAxios();
  return axios
    .post<ConnectToPosResponse>(`${BASE}/connect`, {
      businessId,
      posProvider,
      ...(credentials && { credentials }),
    })
    .then((res) => res.data);
}

// PUT /pos/connections/{posConnectionId}
export async function updatePosConnection(
  posConnectionId: string,
  businessId: string,
  credentials: PosCredentials
): Promise<ConnectToPosResponse> {
  const axios = await getTokenizedAxios();
  return axios
    .put<ConnectToPosResponse>(`${BASE}/connections/${posConnectionId}`, {
      businessId,
      credentials,
    })
    .then((res) => res.data);
}

// GET /pos/tables?businessId=...
export async function fetchTables(businessId: string): Promise<PosTable[]> {
  const axios = await getTokenizedAxios();
  return axios
    .get<{ tables: PosTable[] }>(`${BASE}/tables`, { params: { businessId } })
    .then((res) => res.data.tables);
}

// GET /pos/order?businessId=...&posTableId=...&posTableMetadata=...
// Returns null when the server responds with 404 (no active order).
export async function fetchOrder(
  businessId: string,
  posTableId: string,
  posTableMetadata: Record<string, unknown>
): Promise<PosOrder | null> {
  const axios = await getTokenizedAxios();
  return axios
    .get<{ order: PosOrder }>(`${BASE}/order`, {
      params: {
        businessId,
        posTableId,
        posTableMetadata: JSON.stringify(posTableMetadata),
      },
    })
    .then((res) => res.data.order)
    .catch((err) => {
      // Raw axios error: check status directly.
      // After the response interceptor: err is err.response.data (a plain object with no .stack).
      if (err?.response?.status === 404 || (err && !err.stack)) return null;
      throw err;
    });
}

// GET /pos/items?businessId=...
export async function fetchItems(businessId: string): Promise<PosItem[]> {
  const axios = await getTokenizedAxios();
  return axios
    .get<{ items: PosItem[] }>(`${BASE}/items`, { params: { businessId } })
    .then((res) => res.data.items);
}

// POST /pos/order
export async function createOrder(
  businessId: string,
  posTableId: string,
  posTableMetadata: Record<string, unknown>,
  items: ManualOrderItem[],
  depositAmount?: number
): Promise<{ posOrderId: string }> {
  const axios = await getTokenizedAxios();
  return axios
    .post<{ posOrderId: string }>(`${BASE}/order`, {
      businessId,
      posTableId,
      posTableMetadata,
      items: items.map(
        ({
          articleId,
          productReference,
          name,
          quantity,
          unitPrice,
          vatRate,
        }) => ({
          ...(articleId != null && { articleId }),
          ...(productReference != null && { productReference }),
          name,
          quantity,
          ...(unitPrice != null && { unitPrice }),
          ...(vatRate != null && { vatRate }),
        })
      ),
      ...(depositAmount != null && depositAmount > 0 && { depositAmount }),
    })
    .then((res) => res.data);
}

// POST /pos/order/pay
export async function payOrder(
  businessId: string,
  posOrderId: string,
  posTableMetadata: Record<string, unknown>,
  amount: number
): Promise<void> {
  const axios = await getTokenizedAxios();
  await axios.post(`${BASE}/order/pay`, {
    businessId,
    posOrderId,
    posTableMetadata,
    amount,
  });
}
